import React from 'react';

import { useCanOpenStudio } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import ContextMenu, { Option, Break, Info } from 'kingpin/atoms/ContextMenu';

import PERMISSIONS from '../../permissionDefinitions';
import PermissionGates from '../PermissionGates';
import ConvertReportToDashboardButton from '../ConvertReportToDashboardButton';
import FeatureGate, { FEATURE_GATES } from '../FeatureGate';
import FleetOpsStaffOnly from '../Common/FleetOpsStaffOnly';
import EditInStudioOption from '../EditInStudioOption';
import useReportAnIssueModal from '../ReportAnIssue/useReportAnIssueModal';
import V3UnpublishReport from './V3UnpublishReport';
import PopupReportInfo from './PopupReportInfo';

const ReportMenu = ({
  onStartEditing,
  onCreateNewGadgetClicked,
  onMetricListAdded,
  onAddScorecardClicked,
  onImageCardAdded,
  openVideoCardForm,
  openTextCardForm,
  openIFrameForm,
  onOpenSettings,
  onOpenQuickFilters,
  onOpenPinnedFilters,
  onSaveAs,
  onDelete,
  isValidationReport,
  onValidationReportRemoved,
  openShowUsage,
  onOpenSchedule,
  report,
}: {
  onStartEditing: () => void;
  onCreateNewGadgetClicked?: () => void;
  onMetricListAdded: () => void;
  onAddScorecardClicked: () => void;
  onImageCardAdded: () => void;
  openVideoCardForm: () => void;
  openTextCardForm: () => void;
  openIFrameForm: () => void;
  onOpenSettings: () => void;
  onOpenQuickFilters: () => void;
  onOpenPinnedFilters: () => void;
  onSaveAs: () => void;
  onDelete: () => void;
  isValidationReport: boolean;
  onValidationReportRemoved: () => void;
  openShowUsage: () => void;
  onOpenSchedule: () => void;
  report?: ReportType;
}) => {
  const { open: openReportAnIssue, modal: reportAnIssueModal } =
    useReportAnIssueModal({ type: 'report' });
  const canOpenStudio = useCanOpenStudio();
  return (
    <>
      <ContextMenu testId={'report-menu'}>
        <>
          <PermissionGates.Has
            requiredPermission={
              PERMISSIONS.CONTENT_ACCESS.SET_ANY_AVAILABLE_TO_USERS_OR_PUBLIC
            }
          >
            <Option label={'Settings'} onClick={onOpenSettings} />
          </PermissionGates.Has>
          <PermissionGates.Has
            requiredPermission={PERMISSIONS.REPORT_MANAGEMENT.EDIT_LAYOUT}
          >
            <Option label={'Quick Filters'} onClick={onOpenQuickFilters} />
          </PermissionGates.Has>
          <PermissionGates.Has
            requiredPermission={PERMISSIONS.REPORT_MANAGEMENT.EDIT_LAYOUT}
          >
            <Option label={'Pinned Filters'} onClick={onOpenPinnedFilters} />
          </PermissionGates.Has>
          <PermissionGates.Has
            requiredPermission={PERMISSIONS.REPORT_MANAGEMENT.EDIT_LAYOUT}
          >
            {report ? (
              <EditInStudioOption
                contentType="report"
                id={report.id}
                onClassicEdit={onStartEditing}
              />
            ) : (
              <Option label={'Edit'} onClick={onStartEditing} />
            )}
          </PermissionGates.Has>
          {/* Where Edit opens Studio, content is added there */}
          {!canOpenStudio && (
            <PermissionGates.Has
              requiredPermission={
                PERMISSIONS.REPORT_MANAGEMENT.ADD_REMOVE_VISUALISATIONS
              }
            >
              <Option
                label={'Add Visualization'}
                subMenuPlacement={'left'}
                subMenu={
                  <>
                    <Option
                      label={'Create new gadget'}
                      onClick={onCreateNewGadgetClicked}
                    />
                    <Option
                      label={'Add metric list'}
                      onClick={onMetricListAdded}
                    />
                    <Option
                      label={'Add KPI List'}
                      onClick={onAddScorecardClicked}
                    />
                    <Option
                      label={'Add image card'}
                      onClick={onImageCardAdded}
                    />
                    <Option
                      label={'Add video card'}
                      onClick={openVideoCardForm}
                    />
                    <Option
                      label={'Add text card'}
                      onClick={openTextCardForm}
                    />
                    <Option label={'Add IFrame'} onClick={openIFrameForm} />
                  </>
                }
              />
            </PermissionGates.Has>
          )}
          {isValidationReport && (
            <PermissionGates.Has
              requiredPermission={
                PERMISSIONS.DATA_MANAGEMENT.ADD_VALIDATION_REPORT
              }
            >
              <Option
                label={'Remove validation report'}
                onClick={onValidationReportRemoved}
              />
            </PermissionGates.Has>
          )}
          <FeatureGate featureName={FEATURE_GATES.SCHEDULED_REPORTS}>
            <PermissionGates.Has
              requiredPermission={PERMISSIONS.REPORT_MANAGEMENT.COPY}
            >
              <Option label={'Schedule'} onClick={onOpenSchedule} />
            </PermissionGates.Has>
          </FeatureGate>
          <PermissionGates.Has
            requiredPermission={PERMISSIONS.REPORT_MANAGEMENT.COPY}
          >
            <Option label={'Save as'} onClick={onSaveAs} />
          </PermissionGates.Has>
          <PermissionGates.Has
            requiredPermission={PERMISSIONS.REPORT_MANAGEMENT.COPY}
          >
            <ConvertReportToDashboardButton />
          </PermissionGates.Has>
          <FleetOpsStaffOnly>
            <Option
              label={'Report an issue'}
              onClick={openReportAnIssue}
              testId="report-an-issue"
            />
          </FleetOpsStaffOnly>
          <Option label={'Show Usage'} onClick={openShowUsage} />
          {!!report && <Info {...report} />}
          {!!report && (
            <FleetOpsStaffOnly>
              <PopupReportInfo report={report} />
            </FleetOpsStaffOnly>
          )}
          {!!report && <V3UnpublishReport report={report} />}
          <PermissionGates.Has
            requiredPermission={PERMISSIONS.REPORT_MANAGEMENT.DELETE}
          >
            <Break />
            <Option label={'Delete'} onClick={onDelete} isDanger />
          </PermissionGates.Has>
        </>
      </ContextMenu>
      {reportAnIssueModal}
    </>
  );
};

export default ReportMenu;
